import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Auto-capitalize the first letter of every word */
const autoCapitalize = (str) =>
  str.replace(/\b\w/g, (c) => c.toUpperCase());

/** Normalize a URL → root domain only (e.g. stripe.com) */
const normalizeUrl = (raw) => {
  let url = raw.trim();
  // Add protocol so the URL constructor can parse it
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  try {
    const { hostname } = new URL(url);
    // Strip "www."
    return hostname.replace(/^www\./i, '');
  } catch {
    // If it still fails, just strip obvious prefixes manually
    return raw
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0]
      .split('?')[0];
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SpillTeaReviewPage = () => {
  const navigate = useNavigate();
  const { companyName: companyParam } = useParams();
  const { user, loading } = useAuth();

  // ---- Form state ----
  const [companyName, setCompanyName] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [duration, setDuration] = useState('');
  const [interviewQuestions, setInterviewQuestions] = useState(['']);
  const [jobExperience, setJobExperience] = useState('');
  const [spilledMilkTea, setSpilledMilkTea] = useState('');
  const [bobaRating, setBobaRating] = useState(0);

  // ---- UI state ----
  const [resumeLoaded, setResumeLoaded] = useState(false);
  const [bounceTriggered, setBounceTriggered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const urlInputRef = useRef(null);

  // ---- Auth redirect ----
  useEffect(() => {
    if (!loading && !user) navigate('/connect-email');
  }, [user, loading, navigate]);

  // ---- Seed company name from URL param ----
  useEffect(() => {
    if (companyParam) {
      setCompanyName(decodeURIComponent(companyParam));
    }
  }, [companyParam]);

  // ---- Auto-fill from resume ----
  useEffect(() => {
    const loadResume = async () => {
      if (!user || resumeLoaded) return;

      let resumeData = null;

      // Source 1: user_resumes
      try {
        const { data } = await supabase
          .from('user_resumes')
          .select('extracted_data')
          .eq('user_id', user.id)
          .order('uploaded_at', { ascending: false })
          .limit(1)
          .single();
        if (data?.extracted_data) resumeData = data.extracted_data;
      } catch {
        // ignore
      }

      // Source 2: career_roadmaps fallback
      if (!resumeData) {
        try {
          const { data } = await supabase
            .from('career_roadmaps')
            .select('form_data')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();
          if (data?.form_data?.resume_data) resumeData = data.form_data.resume_data;
        } catch {
          // ignore
        }
      }

      if (resumeData) {
        // Position title
        if (!positionTitle && resumeData.current_title) {
          setPositionTitle(resumeData.current_title);
        }

        // Try to find duration for the selected company
        const decodedCompany = companyParam ? decodeURIComponent(companyParam).toLowerCase() : '';
        if (decodedCompany && Array.isArray(resumeData.work_experience)) {
          const match = resumeData.work_experience.find(
            (w) =>
              (w.company || w.employer || w.organization || '')
                .toLowerCase()
                .includes(decodedCompany) ||
              decodedCompany.includes(
                (w.company || w.employer || w.organization || '').toLowerCase()
              )
          );
          if (match) {
            if (match.title || match.position) setPositionTitle(match.title || match.position);
            if (match.duration) setDuration(match.duration);
          }
        }

        // Fallback duration from years_of_experience
        if (!duration && resumeData.years_of_experience) {
          // Don't auto-fill total years — it's for one company
        }
      }

      setResumeLoaded(true);
    };

    if (user) loadResume();
  }, [user]); // eslint-disable-line

  // ---- Boba rating bounce animation ----
  useEffect(() => {
    if (bobaRating > 0) {
      setBounceTriggered(true);
      const timer = setTimeout(() => setBounceTriggered(false), 800);
      return () => clearTimeout(timer);
    }
  }, [bobaRating]);

  // ---- Handlers ----

  const handleCompanyBlur = () => {
    if (companyName && companyName[0] !== companyName[0].toUpperCase()) {
      setCompanyName(autoCapitalize(companyName));
    }
  };

  const handleUrlBlur = () => {
    if (publicUrl.trim()) {
      setPublicUrl(normalizeUrl(publicUrl));
    }
  };

  const addInterviewQuestion = () => {
    setInterviewQuestions((prev) => [...prev, '']);
  };

  const removeInterviewQuestion = (idx) => {
    setInterviewQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateInterviewQuestion = (idx, value) => {
    setInterviewQuestions((prev) => {
      const copy = [...prev];
      copy[idx] = value;
      return copy;
    });
  };

  const isFormValid =
    companyName.trim() &&
    publicUrl.trim() &&
    positionTitle.trim() &&
    jobExperience.trim() &&
    bobaRating > 0;

  const handleSubmit = async () => {
    if (!isFormValid || !user) return;
    setSubmitting(true);

    try {
      const normalizedDomain = publicUrl.trim() ? normalizeUrl(publicUrl) : '';

      const { error } = await supabase.from('spill_tea_reviews').insert({
        user_id: user.id,
        company_name: companyName.trim(),
        company_domain: normalizedDomain,
        position_title: positionTitle.trim(),
        duration: duration || null,
        interview_questions: interviewQuestions.filter((q) => q.trim()),
        job_experience: jobExperience.trim(),
        spilled_milk_tea: spilledMilkTea.trim() || null,
        boba_rating: bobaRating,
      });

      if (error) throw error;

      // Navigate to the Spill Tea home (now unlocked)
      navigate('/spill-tea/home', { replace: true });
    } catch (err) {
      console.error('Error saving review:', err);
      setSubmitting(false);
    }
  };

  // ---- Duration options ----
  const durationOptions = [
    '', 'Less than 3 months', '3–6 months', '6–12 months',
    '1–2 years', '2–3 years', '3–5 years', '5–10 years', '10+ years',
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // ---- Render ----
  return (
    <div
      className="min-h-screen overflow-x-hidden pb-12"
      style={{ position: 'relative', fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* Fixed sky background */}
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url(/bluewhite.png)', backgroundSize: 'cover',
          backgroundPosition: 'center', zIndex: -1,
          transform: 'translateZ(0)', willChange: 'transform',
        }}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');

        @keyframes bobaBounce {
          0%   { transform: translateY(0); }
          30%  { transform: translateY(-10px); }
          50%  { transform: translateY(0); }
          70%  { transform: translateY(-4px); }
          100% { transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-xl"
        style={{
          background: 'rgba(255, 252, 248, 0.65)',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
          WebkitBackdropFilter: 'blur(20px)', backdropFilter: 'blur(20px)',
        }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="icon"
              onClick={() => navigate('/spill-tea')}
              className="rounded-xl shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '1.3rem' }}>🍵</span>
              <h1
                className="text-xl font-bold"
                style={{ fontFamily: "'Montserrat', sans-serif", color: '#2d1b0e' }}
              >
                Write Your Review
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">

        {/* ============================================================= */}
        {/* Glass card wrapper */}
        {/* ============================================================= */}
        <div
          className="rounded-[28px] overflow-hidden relative"
          style={{
            background: 'linear-gradient(145deg, rgba(200,210,220,0.45) 0%, rgba(180,195,210,0.35) 50%, rgba(170,185,200,0.3) 100%)',
            border: '1px solid rgba(255,255,255,0.5)',
            boxShadow: '0 25px 60px -15px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {/* Glass reflections */}
          <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/30 to-transparent rounded-t-[28px] pointer-events-none" />

          <div className="relative px-5 sm:px-7 py-7 space-y-6">

            {/* ---- Company Name ---- */}
            <Field label="Company Name" required>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onBlur={handleCompanyBlur}
                placeholder="e.g. Stripe"
                className="field-input"
              />
            </Field>

            {/* ---- Public URL ---- */}
            <Field label="Public URL" required>
              <input
                ref={urlInputRef}
                type="text"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                onBlur={handleUrlBlur}
                placeholder="e.g. stripe.com"
                className="field-input"
              />
            </Field>

            {/* ---- Position Title ---- */}
            <Field label="Your Position Title" required>
              <input
                type="text"
                value={positionTitle}
                onChange={(e) => setPositionTitle(e.target.value)}
                placeholder="e.g. Software Engineer"
                className="field-input"
              />
            </Field>

            {/* ---- Duration ---- */}
            <Field label="How long were you at this company?">
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="field-input"
                style={{ appearance: 'none', cursor: 'pointer' }}
              >
                {durationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || 'Select duration…'}
                  </option>
                ))}
              </select>
            </Field>

            {/* ---- Interview Questions ---- */}
            <div>
              <label className="field-label">Interview Questions</label>
              <div className="space-y-2 mt-1.5">
                {interviewQuestions.map((q, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold shrink-0"
                      style={{ color: 'rgba(255,255,255,0.5)', width: '20px', textAlign: 'right' }}
                    >
                      {idx + 1}.
                    </span>
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => updateInterviewQuestion(idx, e.target.value)}
                      placeholder="Type a question you were asked…"
                      className="field-input flex-1"
                    />
                    {interviewQuestions.length > 1 && (
                      <button
                        onClick={() => removeInterviewQuestion(idx)}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.4)' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={addInterviewQuestion}
                className="mt-2 flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl hover:bg-white/10 transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Inter', sans-serif" }}
              >
                <Plus className="w-4 h-4" /> Add question
              </button>
            </div>

            {/* ---- Job Experience ---- */}
            <Field label="Job Experience" required>
              <textarea
                value={jobExperience}
                onChange={(e) => setJobExperience(e.target.value)}
                rows={5}
                placeholder={"• Day-to-day reality\n• Management & Feedback\n• Growth & Why you exited the role"}
                className="field-input resize-none"
              />
            </Field>

            {/* ---- Spilled Milk Tea ---- */}
            <Field label="Spill the Milk Tea 🧋" subtitle="What you've heard about it (alleged)">
              <textarea
                value={spilledMilkTea}
                onChange={(e) => setSpilledMilkTea(e.target.value)}
                rows={3}
                placeholder="Rumors or Things people talk about..."
                className="field-input resize-none"
              />
            </Field>

            {/* ---- Boba Rating ---- */}
            <div>
              <label className="field-label">
                Rate Experience <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
              </label>
              <p
                className="text-xs mt-0.5 mb-3"
                style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}
              >
                Tap a boba to rate 1–10
              </p>

              <div className="flex items-end gap-2 justify-center">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
                  const filled = n <= bobaRating;
                  const shouldBounce = bounceTriggered && filled;
                  return (
                    <div
                      key={n}
                      onClick={() => setBobaRating(n)}
                      className="flex flex-col items-center cursor-pointer group"
                      style={{
                        animation: shouldBounce
                          ? `bobaBounce 0.5s ease ${(n - 1) * 0.06}s both`
                          : 'none',
                      }}
                    >
                      {/* Boba circle */}
                      <div
                        className="rounded-full transition-all duration-200"
                        style={{
                          width: 32,
                          height: 32,
                          background: filled
                            ? 'linear-gradient(145deg, #5C3A1E, #8B6914)'
                            : 'rgba(255,255,255,0.15)',
                          border: `2px solid ${filled ? '#8B6914' : 'rgba(255,255,255,0.25)'}`,
                          boxShadow: filled
                            ? 'inset 0 -3px 6px rgba(0,0,0,0.25), 0 2px 8px rgba(92,58,30,0.3)'
                            : 'none',
                        }}
                      />
                      {/* Number label */}
                      <span
                        className="text-[10px] mt-1 font-semibold"
                        style={{
                          color: filled ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)',
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {n}
                      </span>
                    </div>
                  );
                })}
              </div>

              {bobaRating > 0 && (
                <motion.p
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mt-3 text-sm font-bold"
                  style={{ color: 'rgba(255,255,255,0.85)', fontFamily: "'Inter', sans-serif" }}
                >
                  {bobaRating}/10 Boba
                </motion.p>
              )}
            </div>

          </div>
        </div>

        {/* ============================================================= */}
        {/* Submit button */}
        {/* ============================================================= */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || submitting}
            className="w-full py-6 text-base font-bold rounded-2xl transition-all duration-200"
            style={{
              background: isFormValid
                ? 'linear-gradient(135deg, #1e3a5f, #2563eb)'
                : 'rgba(255,255,255,0.15)',
              color: isFormValid ? '#fff' : 'rgba(255,255,255,0.4)',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '1.05rem',
              letterSpacing: '0.02em',
              border: 'none',
              boxShadow: isFormValid ? '0 8px 30px rgba(37,99,235,0.3)' : 'none',
              cursor: isFormValid ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>Time to Stalk</>
            )}
          </Button>
        </motion.div>

      </main>

      {/* Shared field styles */}
      <style>{`
        .field-label {
          display: block;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.88);
          margin-bottom: 6px;
        }

        .field-input {
          width: 100%;
          padding: 12px 16px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.75);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          color: #1a1a1a;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .field-input::placeholder {
          color: #9ca3af;
        }
        .field-input:focus {
          border-color: rgba(37,99,235,0.5);
          box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
        }

        select.field-input {
          background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%239CA3AF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 40px;
        }

        textarea.field-input {
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reusable field wrapper
// ---------------------------------------------------------------------------
const Field = ({ label, required, subtitle, children }) => (
  <div>
    <label className="field-label">
      {label}
      {required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
    </label>
    {subtitle && (
      <p
        className="text-xs -mt-0.5 mb-1.5"
        style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}
      >
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

export default SpillTeaReviewPage;
